import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export interface Task {
  id: string;
  contractId: string;
  assignedTo: string;
  taskType: 'review' | 'sign' | 'approve';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  title: string;
  description?: string;
  dueDate?: string;
  completedAt?: string;
  completedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export function usePendingTaskCount() {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Get current user
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getUser();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const loadCount = async () => {
      try {
        const { count: taskCount, error } = await supabase
          .from('tasks')
          .select('*', { count: 'exact', head: true })
          .eq('assigned_to', userId)
          .eq('status', 'pending');

        if (error) throw error;
        setCount(taskCount || 0);
        setLoading(false);
      } catch (error) {
        console.error('Error loading task count:', error);
        setLoading(false);
      }
    };

    // Initial load
    loadCount();

    // Set up real-time subscription for task changes
    const channel = supabase
      .channel(`tasks_changes_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `assigned_to=eq.${userId}`,
        },
        (payload) => {
          console.log('Task change detected:', payload);
          // Reload count when any task assigned to this user changes
          loadCount();
        }
      )
      .subscribe((status) => {
        console.log('Task subscription status:', status);
      });

    return () => {
      console.log('Unsubscribing from task changes');
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const reload = async () => {
    if (!userId) return;
    
    try {
      const { count: taskCount, error } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('assigned_to', userId)
        .eq('status', 'pending');

      if (error) throw error;
      setCount(taskCount || 0);
    } catch (error) {
      console.error('Error loading task count:', error);
    }
  };

  return { count, loading, reload };
}

export function usePendingTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTasks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          contracts (
            client_name,
            status
          )
        `)
        .eq('assigned_to', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedTasks = (data || []).map((row: any) => ({
        id: row.id,
        contractId: row.contract_id,
        assignedTo: row.assigned_to,
        taskType: row.task_type,
        status: row.status,
        priority: row.priority,
        title: row.title,
        description: row.description,
        dueDate: row.due_date,
        completedAt: row.completed_at,
        completedBy: row.completed_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        contract: row.contracts,
      }));

      setTasks(transformedTasks as Task[]);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  return { tasks, loading, reload: loadTasks };
}

